import React, {
    CSSProperties,
    forwardRef,
    KeyboardEvent,
    MouseEventHandler,
    ReactNode,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState
} from "react";
import classNames from "classnames";

import {useIsMobile, useIsMounted, useStateRef} from "@pg-mono/hooks";

import {SLIDER_GALLERY_ROOT_ATTR} from "../constants/gesture";
import {GestureDirection, useSliderTouchEvents} from "../hooks/use_slider_touch_events";
import {SliderGallerySlideNavigation} from "../types/SliderGallerySlideNavigation";
import {getClosestBreakpoint} from "../utils/get_closest_breakpoint";

import {arrow, arrowDisabled, galleryHolder, galleryHolderFlush, singleSlide, singleSlideFlush, slider, sliderInnerWrap} from "./BaseSlider.module.css";

export type ISliderTransition = "ease" | "linear" | "ease-in" | "ease-out" | "ease-in-out";
export type IBreakpointSettings = {breakpoint: number; slidesToShow: number; slidesToScroll: number};

export interface IBaseSliderHandle {
    goToSlide: (slide: number) => void;
    slideLeft: () => void;
    slideRight: () => void;
}

interface IProps {
    arrows?: {
        left: React.JSX.Element;
        right: React.JSX.Element;
    };
    className?: string;
    clonedSlidesCount?: number;
    disableKeyboardEvents?: boolean;
    disableSwipe?: boolean;
    /** Drops the default 1px slide inset so the previous slide does not peek at the edge. */
    flushEdges?: boolean;
    forceInitialize?: true;
    initialSlide?: number;
    onResizeDelay?: number; // milliseconds
    onSlideChange?: (currentSlideIndex: number, nextSlideIndex: number, navigation?: SliderGallerySlideNavigation) => void;
    preventInfinityScroll?: boolean;
    preventLazyLoad?: boolean;
    sliderTransition?: ISliderTransition;
    sliderWrapperClass?: string;
    slides: ReactNode[];
    slideStyles?: CSSProperties | CSSProperties[];
    swipeDeadband?: number;

    /// slick-slider-like options:
    breakpoints?: IBreakpointSettings[];
    directionalPeek?: boolean;
    slidesToScroll?: number;
    slidesToShow?: number;
    speed?: number;
}

const DEFAULT_SPEED = 300;
const DEFAULT_SLIDER_TRANSITION = "ease-out";
const DEFAULT_CLONED_SLIDES_COUNT = 2;
const DEFAULT_SLIDES_TO_SHOW = 1;
const DEFAULT_SLIDES_TO_SCROLL = 1;

// Wait for paint before the next transform step (e.g. instant clone skip, then animated wrap).
const scheduleAfterPaint = (callback: () => void) => {
    requestAnimationFrame(() => {
        requestAnimationFrame(callback);
    });
};

// I am using a lot of refs here to avoid potential issues with stale state being used inside event listeners. Reference to equal state value will always be up to date.

export const BaseSlider = forwardRef<IBaseSliderHandle, IProps>((props, ref) => {
    const {
        arrows,
        breakpoints,
        className,
        clonedSlidesCount = DEFAULT_CLONED_SLIDES_COUNT,
        directionalPeek = false,
        disableKeyboardEvents,
        disableSwipe,
        flushEdges = false,
        initialSlide = 0,
        onResizeDelay,
        onSlideChange,
        preventInfinityScroll = clonedSlidesCount === 0,
        sliderTransition = DEFAULT_SLIDER_TRANSITION,
        sliderWrapperClass,
        slides,
        slidesToScroll = DEFAULT_SLIDES_TO_SCROLL,
        slidesToShow = DEFAULT_SLIDES_TO_SHOW,
        slideStyles,
        speed = DEFAULT_SPEED,
        swipeDeadband
    } = props;

    const galleryHolderRef = useRef<HTMLDivElement | null>(null);
    const gallerySliderRef = useRef<HTMLDivElement | null>(null);
    const isMobile = useIsMobile(true);
    const isMounted = useIsMounted();
    const isSwipeDisabled = Boolean(disableSwipe) || slides.length <= 1;

    const [isSliderLoaded, setIsSliderLoaded, isSliderLoadedRef] = useStateRef(false);
    const [translateValue, setTranslateValue] = useState(0);
    const [currentIndex, setCurrentIndex, currentIndexRef] = useStateRef(initialSlide);
    const [breakpointSettings, setBreakpointSettings, breakpointSettingsRef] = useStateRef({
        breakpoint: 0,
        slidesToShow,
        slidesToScroll
    });

    const isTransitioning = useRef(false);
    const slideCountRef = useRef(0);
    const preventAnimationRef = useRef(false);
    const pendingSlideNavigationRef = useRef<SliderGallerySlideNavigation | null>(null);
    const disableSwipeRef = useRef(isSwipeDisabled);
    const alignPeekEndRef = useRef(false);
    const preservePeekAlignRef = useRef(false);

    const slidesListWithClones =
        isSliderLoaded && clonedSlidesCount > 0 ? [...slides.slice(-1 * clonedSlidesCount), ...slides, ...slides.slice(0, clonedSlidesCount)] : slides;

    /*
     * initialization, breakpoints and resizing logic
     */

    const setCurrentBreakpointSettings = () => {
        if (!breakpoints) return;

        const windowWidth = window.innerWidth;
        setBreakpointSettings(
            getClosestBreakpoint(
                [
                    {
                        breakpoint: 0,
                        slidesToShow,
                        slidesToScroll
                    },
                    ...breakpoints
                ],
                windowWidth
            )
        );
    };

    const initializeSlider = () => {
        setCurrentBreakpointSettings();

        if (!isSliderLoadedRef.current) {
            setTimeout(() => {
                setIsSliderLoaded(true);
                skipToSlide(currentIndexRef.current);
            }, 0);
        }
    };

    const onResize = () => {
        setCurrentBreakpointSettings();

        const isDelayed = Number.isFinite(onResizeDelay);
        if (isSliderLoaded) {
            isDelayed ? setTimeout(() => goToSlide(currentIndexRef.current), onResizeDelay) : goToSlide(currentIndexRef.current);
        }
    };

    // attach resize events
    useEffect(() => {
        window.addEventListener("resize", onResize);

        return () => {
            window.removeEventListener("resize", onResize);
        };
    }, [currentIndex, isSliderLoaded, breakpointSettings]);

    /*
     * slider logic
     */

    // init
    useEffect(() => {
        if (isMounted) {
            initializeSlider();
        }
    }, [isMounted]);

    // attach keyboard events
    useEffect(() => {
        if (!disableKeyboardEvents) {
            document.addEventListener("keydown", handleSliderKeyDown);
        }
        return () => {
            if (!disableKeyboardEvents) {
                document.removeEventListener("keydown", handleSliderKeyDown);
            }
        };
    }, [disableKeyboardEvents]);

    // Fallback when transitionend does not fire (e.g. iOS). Clone skip has no transform transition — unlock immediately.
    useEffect(() => {
        const isSkippingSlide = preventAnimationRef.current;
        isTransitioning.current = !isSkippingSlide;

        if (isSkippingSlide) return;

        const timeout = setTimeout(() => {
            isTransitioning.current = false;
        }, speed);

        return () => {
            clearTimeout(timeout);
        };
    }, [currentIndex, speed]);

    slideCountRef.current = slides.length;

    const getGalleryWidth = () => {
        const slider = gallerySliderRef.current;

        if (!slider) return 0;

        if (flushEdges) {
            const firstSlide = slider.firstElementChild;

            if (firstSlide instanceof HTMLElement) {
                const slideWidth = firstSlide.getBoundingClientRect().width;

                if (slideWidth > 0) return slideWidth;
            }

            return slider.parentElement?.getBoundingClientRect().width || slider.clientWidth;
        }

        return slider.clientWidth;
    };

    // Sync in an effect (not during render) so the ref reliably reflects committed state under React 19.
    useEffect(() => {
        disableSwipeRef.current = isSwipeDisabled;
    }, [isSwipeDisabled]);

    const onGestureEnd = (gestureDirection: GestureDirection) => {
        if (gestureDirection && !isTransitioning.current) {
            pendingSlideNavigationRef.current = SliderGallerySlideNavigation.SWIPE;
            const nextSlide = currentIndexRef.current + gestureDirection;
            goToSlide(nextSlide);
        }
    };

    const {shouldBlockEventsRef} = useSliderTouchEvents(
        galleryHolderRef,
        initializeSlider,
        onGestureEnd,
        isMobile,
        () => !!disableSwipeRef.current,
        isSwipeDisabled,
        swipeDeadband
    );

    /*
     * slide traversing methods
     */
    const setNewCurrentSlide = (slide: number) => {
        setCurrentIndex(slide);
        const galleryWidth = getGalleryWidth();
        const visibleSlides = breakpointSettingsRef.current.slidesToShow;
        const baseTranslate = ((clonedSlidesCount + slide) * galleryWidth * -slidesToScroll) / visibleSlides;
        const peekOffset = directionalPeek && alignPeekEndRef.current && visibleSlides > 1 ? galleryWidth * (1 - 1 / visibleSlides) : 0;

        setTranslateValue(baseTranslate + peekOffset);
    };

    const skipToSlide = (slide: number) => {
        preventAnimationRef.current = true;
        setNewCurrentSlide(slide);

        scheduleAfterPaint(() => {
            preventAnimationRef.current = false;
        });
    };

    const handleSliderTransitionEnd = (event: React.TransitionEvent<HTMLDivElement>) => {
        if (event.target !== event.currentTarget || event.propertyName !== "transform") return;

        isTransitioning.current = false;
    };

    /**
     * The peek normally follows the travel direction: forward peeks at the next slide, back at the previous one.
     * Without wrap-around the outer slides have no neighbour on that side and would peek at empty space,
     * so they peek at the neighbour they do have instead.
     */
    const getAlignPeekEnd = (slide: number) => {
        const lastPeekableIndex = Math.max(slideCountRef.current - Math.floor(breakpointSettingsRef.current.slidesToShow), 0);

        if (preventInfinityScroll && slide <= 0) return false;
        if (preventInfinityScroll && slide >= lastPeekableIndex) return true;

        return slide < 0 || slide < currentIndexRef.current;
    };

    const goToSlide = (slide: number) => {
        const slideCount = slideCountRef.current;
        const lastSlideIndex = slideCount - 1;
        const {slidesToScroll: slidesToScrollSetting} = breakpointSettingsRef.current;

        if (directionalPeek && !preservePeekAlignRef.current && slide !== currentIndexRef.current) {
            alignPeekEndRef.current = getAlignPeekEnd(slide);
        }

        if (slide < 0 || slide > lastSlideIndex) {
            if (preventInfinityScroll) return;

            isTransitioning.current = true;

            const slideToSkipToBeforeAnim = slide < 0 ? slideCount + currentIndexRef.current : slide - slideCount - slidesToScrollSetting;
            const nextSlide = slide < 0 ? slideToSkipToBeforeAnim - slidesToScrollSetting : slideToSkipToBeforeAnim + slidesToScrollSetting;

            skipToSlide(slideToSkipToBeforeAnim);

            scheduleAfterPaint(() => {
                preservePeekAlignRef.current = true;
                goToSlide(nextSlide);
                preservePeekAlignRef.current = false;
            });

            return;
        }

        if (currentIndexRef.current !== slide) {
            onSlideChange?.(currentIndexRef.current, slide, pendingSlideNavigationRef.current ?? undefined);
            pendingSlideNavigationRef.current = null;
            // Lock only on a real slide change. On resize (e.g. mobile address bar hiding on scroll)
            // goToSlide(currentIndex) runs with no transition, so nothing would ever unlock it.
            isTransitioning.current = true;
        }

        setNewCurrentSlide(slide);
    };

    /*
     * slide traversing - arrow click + keyboard event handlers
     */

    const handleSliderKeyDown = useCallback((e: Event) => {
        // casting as unknown because "keydown" event does not accept KeyDownEventHandler O_o . I gave up
        const keyboardEvent = e as unknown as KeyboardEvent;
        if (keyboardEvent.key === "ArrowLeft") slideLeft();
        else if (keyboardEvent.key === "ArrowRight") slideRight();
    }, []);

    const slideRight = useCallback(() => {
        if (shouldBlockEventsRef.current || isTransitioning.current) return;

        const newSlide = currentIndexRef.current + breakpointSettingsRef.current.slidesToScroll;
        // slideCount - 1 is only correct when slidesToShow is 1 - with more slides visible at once,
        // navigating that far reveals empty space past the last slide (the viewport window extends
        // beyond the slide strip). The last index that still fills the viewport with real slides is
        // slideCount - slidesToShow, floored since slide indices are integers.
        const lastReachableIndex = Math.max(slideCountRef.current - Math.floor(breakpointSettingsRef.current.slidesToShow), 0);
        if (preventInfinityScroll && newSlide > lastReachableIndex) return;

        pendingSlideNavigationRef.current = SliderGallerySlideNavigation.ARROW_NEXT;
        goToSlide(newSlide);
    }, []);

    const handleSlideRightOnClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        slideRight();
    };

    const slideLeft = useCallback(() => {
        if (shouldBlockEventsRef.current || isTransitioning.current) return;

        const newSlide = currentIndexRef.current - breakpointSettingsRef.current.slidesToScroll;
        if (preventInfinityScroll && newSlide < 0) return;

        pendingSlideNavigationRef.current = SliderGallerySlideNavigation.ARROW_PREV;
        goToSlide(newSlide);
    }, []);

    const handleSlideLeftOnClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        slideLeft();
    };

    useImperativeHandle(ref, () => ({
        goToSlide,
        slideLeft,
        slideRight
    }));

    /*
     * misc
     */

    const preventClickEventsOnGesture: MouseEventHandler = (e) => {
        // prevent click events when gesture is detected
        if (shouldBlockEventsRef.current) {
            e.preventDefault();
            e.stopPropagation();
        }
    };

    /*
     * render
     */

    const slidesToShowDynamic = breakpointSettingsRef.current.slidesToShow;
    const transitionStyle: CSSProperties = !preventAnimationRef.current
        ? {transform: `translateX(${translateValue}px)`, transition: `transform ${speed}ms ${sliderTransition}`}
        : {transform: `translateX(${translateValue}px)`};
    const slideWidthPercent = slidesToShowDynamic ? 100 / slidesToShowDynamic : 100;
    const slideWidth = `${slideWidthPercent}%`;
    const singleSlideWidth: CSSProperties = {
        flex: `0 0 ${slideWidth}`,
        maxWidth: slideWidth,
        minWidth: `max(${slideWidth}, var(--base-slider-slide-min-width, 0px))`
    };
    const galleryHolderCN = classNames(className, galleryHolder, flushEdges && galleryHolderFlush);
    const sliderCN = classNames(className, slider);
    const singleSliderCN = classNames(slideStyles, singleSlide, flushEdges && singleSlideFlush);
    const sliderWrapperCN = classNames(sliderInnerWrap, sliderWrapperClass);

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === slides.length - 1;
    const leftArrowCN = classNames(arrow, preventInfinityScroll && isFirstSlide && arrowDisabled);
    const rightArrowCN = classNames(arrow, preventInfinityScroll && isLastSlide && arrowDisabled);

    return (
        <div {...{[SLIDER_GALLERY_ROOT_ATTR]: ""}} className={galleryHolderCN} onClick={preventClickEventsOnGesture} ref={galleryHolderRef}>
            {arrows && slides.length > 1 && (
                <span onClick={handleSlideLeftOnClick} className={leftArrowCN}>
                    {arrows.left}
                </span>
            )}
            <div className={sliderWrapperCN}>
                {!isSliderLoaded ? (
                    <div ref={gallerySliderRef} className={sliderCN} style={transitionStyle}>
                        <div key={clonedSlidesCount} className={singleSliderCN} style={singleSlideWidth}>
                            {slides[initialSlide]}
                        </div>
                    </div>
                ) : (
                    <div ref={gallerySliderRef} className={sliderCN} style={transitionStyle} onTransitionEnd={handleSliderTransitionEnd}>
                        {slidesListWithClones.map((slide, index) => (
                            <div key={isSliderLoaded ? index + clonedSlidesCount : index} className={singleSliderCN} style={singleSlideWidth}>
                                {slide}
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {arrows && slides.length > 1 && (
                <span onClick={handleSlideRightOnClick} className={rightArrowCN}>
                    {arrows.right}
                </span>
            )}
        </div>
    );
});

BaseSlider.displayName = "BaseSlider";
import React, {
    CSSProperties,
    forwardRef,
    KeyboardEvent,
    MouseEventHandler,
    ReactNode,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState
} from "react";
import classNames from "classnames";

import {useIsMobile, useIsMounted, useStateRef} from "@pg-mono/hooks";

import {SLIDER_GALLERY_ROOT_ATTR} from "../constants/gesture";
import {GestureDirection, useSliderTouchEvents} from "../hooks/use_slider_touch_events";
import {SliderGallerySlideNavigation} from "../types/SliderGallerySlideNavigation";
import {getClosestBreakpoint} from "../utils/get_closest_breakpoint";

import {arrow, arrowDisabled, galleryHolder, galleryHolderFlush, singleSlide, singleSlideFlush, slider, sliderInnerWrap} from "./BaseSlider.module.css";

export type ISliderTransition = "ease" | "linear" | "ease-in" | "ease-out" | "ease-in-out";
export type IBreakpointSettings = {breakpoint: number; slidesToShow: number; slidesToScroll: number};

export interface IBaseSliderHandle {
    goToSlide: (slide: number) => void;
    slideLeft: () => void;
    slideRight: () => void;
}

interface IProps {
    arrows?: {
        left: React.JSX.Element;
        right: React.JSX.Element;
    };
    className?: string;
    clonedSlidesCount?: number;
    disableKeyboardEvents?: boolean;
    disableSwipe?: boolean;
    /** Drops the default 1px slide inset so the previous slide does not peek at the edge. */
    flushEdges?: boolean;
    forceInitialize?: true;
    initialSlide?: number;
    onResizeDelay?: number; // milliseconds
    onSlideChange?: (currentSlideIndex: number, nextSlideIndex: number, navigation?: SliderGallerySlideNavigation) => void;
    preventInfinityScroll?: boolean;
    preventLazyLoad?: boolean;
    sliderTransition?: ISliderTransition;
    sliderWrapperClass?: string;
    slides: ReactNode[];
    slideStyles?: CSSProperties | CSSProperties[];
    swipeDeadband?: number;

    /// slick-slider-like options:
    breakpoints?: IBreakpointSettings[];
    directionalPeek?: boolean;
    slidesToScroll?: number;
    slidesToShow?: number;
    speed?: number;
}

const DEFAULT_SPEED = 300;
const DEFAULT_SLIDER_TRANSITION = "ease-out";
const DEFAULT_CLONED_SLIDES_COUNT = 2;
const DEFAULT_SLIDES_TO_SHOW = 1;
const DEFAULT_SLIDES_TO_SCROLL = 1;

// Wait for paint before the next transform step (e.g. instant clone skip, then animated wrap).
const scheduleAfterPaint = (callback: () => void) => {
    requestAnimationFrame(() => {
        requestAnimationFrame(callback);
    });
};

// I am using a lot of refs here to avoid potential issues with stale state being used inside event listeners. Reference to equal state value will always be up to date.

export const BaseSlider = forwardRef<IBaseSliderHandle, IProps>((props, ref) => {
    const {
        arrows,
        breakpoints,
        className,
        clonedSlidesCount = DEFAULT_CLONED_SLIDES_COUNT,
        directionalPeek = false,
        disableKeyboardEvents,
        disableSwipe,
        flushEdges = false,
        initialSlide = 0,
        onResizeDelay,
        onSlideChange,
        preventInfinityScroll = clonedSlidesCount === 0,
        sliderTransition = DEFAULT_SLIDER_TRANSITION,
        sliderWrapperClass,
        slides,
        slidesToScroll = DEFAULT_SLIDES_TO_SCROLL,
        slidesToShow = DEFAULT_SLIDES_TO_SHOW,
        slideStyles,
        speed = DEFAULT_SPEED,
        swipeDeadband
    } = props;

    const galleryHolderRef = useRef<HTMLDivElement | null>(null);
    const gallerySliderRef = useRef<HTMLDivElement | null>(null);
    const isMobile = useIsMobile(true);
    const isMounted = useIsMounted();
    const isSwipeDisabled = Boolean(disableSwipe) || slides.length <= 1;

    const [isSliderLoaded, setIsSliderLoaded, isSliderLoadedRef] = useStateRef(false);
    const [translateValue, setTranslateValue] = useState(0);
    const [currentIndex, setCurrentIndex, currentIndexRef] = useStateRef(initialSlide);
    const [breakpointSettings, setBreakpointSettings, breakpointSettingsRef] = useStateRef({
        breakpoint: 0,
        slidesToShow,
        slidesToScroll
    });

    const isTransitioning = useRef(false);
    const slideCountRef = useRef(0);
    const preventAnimationRef = useRef(false);
    const pendingSlideNavigationRef = useRef<SliderGallerySlideNavigation | null>(null);
    const disableSwipeRef = useRef(isSwipeDisabled);
    const alignPeekEndRef = useRef(false);
    const preservePeekAlignRef = useRef(false);

    const slidesListWithClones =
        isSliderLoaded && clonedSlidesCount > 0 ? [...slides.slice(-1 * clonedSlidesCount), ...slides, ...slides.slice(0, clonedSlidesCount)] : slides;

    /*
     * initialization, breakpoints and resizing logic
     */

    const setCurrentBreakpointSettings = () => {
        if (!breakpoints) return;

        const windowWidth = window.innerWidth;
        setBreakpointSettings(
            getClosestBreakpoint(
                [
                    {
                        breakpoint: 0,
                        slidesToShow,
                        slidesToScroll
                    },
                    ...breakpoints
                ],
                windowWidth
            )
        );
    };

    const initializeSlider = () => {
        setCurrentBreakpointSettings();

        if (!isSliderLoadedRef.current) {
            setTimeout(() => {
                setIsSliderLoaded(true);
                skipToSlide(currentIndexRef.current);
            }, 0);
        }
    };

    const onResize = () => {
        setCurrentBreakpointSettings();

        const isDelayed = Number.isFinite(onResizeDelay);
        if (isSliderLoaded) {
            isDelayed ? setTimeout(() => goToSlide(currentIndexRef.current), onResizeDelay) : goToSlide(currentIndexRef.current);
        }
    };

    // attach resize events
    useEffect(() => {
        window.addEventListener("resize", onResize);

        return () => {
            window.removeEventListener("resize", onResize);
        };
    }, [currentIndex, isSliderLoaded, breakpointSettings]);

    /*
     * slider logic
     */

    // init
    useEffect(() => {
        if (isMounted) {
            initializeSlider();
        }
    }, [isMounted]);

    // attach keyboard events
    useEffect(() => {
        if (!disableKeyboardEvents) {
            document.addEventListener("keydown", handleSliderKeyDown);
        }
        return () => {
            if (!disableKeyboardEvents) {
                document.removeEventListener("keydown", handleSliderKeyDown);
            }
        };
    }, [disableKeyboardEvents]);

    // Fallback when transitionend does not fire (e.g. iOS). Clone skip has no transform transition — unlock immediately.
    useEffect(() => {
        const isSkippingSlide = preventAnimationRef.current;
        isTransitioning.current = !isSkippingSlide;

        if (isSkippingSlide) return;

        const timeout = setTimeout(() => {
            isTransitioning.current = false;
        }, speed);

        return () => {
            clearTimeout(timeout);
        };
    }, [currentIndex, speed]);

    slideCountRef.current = slides.length;

    const getGalleryWidth = () => {
        const slider = gallerySliderRef.current;

        if (!slider) return 0;

        if (flushEdges) {
            const firstSlide = slider.firstElementChild;

            if (firstSlide instanceof HTMLElement) {
                const slideWidth = firstSlide.getBoundingClientRect().width;

                if (slideWidth > 0) return slideWidth;
            }

            return slider.parentElement?.getBoundingClientRect().width || slider.clientWidth;
        }

        return slider.clientWidth;
    };

    // Sync in an effect (not during render) so the ref reliably reflects committed state under React 19.
    useEffect(() => {
        disableSwipeRef.current = isSwipeDisabled;
    }, [isSwipeDisabled]);

    const onGestureEnd = (gestureDirection: GestureDirection) => {
        if (gestureDirection && !isTransitioning.current) {
            pendingSlideNavigationRef.current = SliderGallerySlideNavigation.SWIPE;
            const nextSlide = currentIndexRef.current + gestureDirection;
            goToSlide(nextSlide);
        }
    };

    const {shouldBlockEventsRef} = useSliderTouchEvents(
        galleryHolderRef,
        initializeSlider,
        onGestureEnd,
        isMobile,
        () => !!disableSwipeRef.current,
        isSwipeDisabled,
        swipeDeadband
    );

    /*
     * slide traversing methods
     */
    const setNewCurrentSlide = (slide: number) => {
        setCurrentIndex(slide);
        const galleryWidth = getGalleryWidth();
        const visibleSlides = breakpointSettingsRef.current.slidesToShow;
        const baseTranslate = ((clonedSlidesCount + slide) * galleryWidth * -slidesToScroll) / visibleSlides;
        const peekOffset = directionalPeek && alignPeekEndRef.current && visibleSlides > 1 ? galleryWidth * (1 - 1 / visibleSlides) : 0;

        setTranslateValue(baseTranslate + peekOffset);
    };

    const skipToSlide = (slide: number) => {
        preventAnimationRef.current = true;
        setNewCurrentSlide(slide);

        scheduleAfterPaint(() => {
            preventAnimationRef.current = false;
        });
    };

    const handleSliderTransitionEnd = (event: React.TransitionEvent<HTMLDivElement>) => {
        if (event.target !== event.currentTarget || event.propertyName !== "transform") return;

        isTransitioning.current = false;
    };

    /**
     * The peek normally follows the travel direction: forward peeks at the next slide, back at the previous one.
     * Without wrap-around the outer slides have no neighbour on that side and would peek at empty space,
     * so they peek at the neighbour they do have instead.
     */
    const getAlignPeekEnd = (slide: number) => {
        const lastPeekableIndex = Math.max(slideCountRef.current - Math.floor(breakpointSettingsRef.current.slidesToShow), 0);

        if (preventInfinityScroll && slide <= 0) return false;
        if (preventInfinityScroll && slide >= lastPeekableIndex) return true;

        return slide < 0 || slide < currentIndexRef.current;
    };

    const goToSlide = (slide: number) => {
        const slideCount = slideCountRef.current;
        const lastSlideIndex = slideCount - 1;
        const {slidesToScroll: slidesToScrollSetting} = breakpointSettingsRef.current;

        if (directionalPeek && !preservePeekAlignRef.current && slide !== currentIndexRef.current) {
            alignPeekEndRef.current = getAlignPeekEnd(slide);
        }

        if (slide < 0 || slide > lastSlideIndex) {
            if (preventInfinityScroll) return;

            isTransitioning.current = true;

            const slideToSkipToBeforeAnim = slide < 0 ? slideCount + currentIndexRef.current : slide - slideCount - slidesToScrollSetting;
            const nextSlide = slide < 0 ? slideToSkipToBeforeAnim - slidesToScrollSetting : slideToSkipToBeforeAnim + slidesToScrollSetting;

            skipToSlide(slideToSkipToBeforeAnim);

            scheduleAfterPaint(() => {
                preservePeekAlignRef.current = true;
                goToSlide(nextSlide);
                preservePeekAlignRef.current = false;
            });

            return;
        }

        if (currentIndexRef.current !== slide) {
            onSlideChange?.(currentIndexRef.current, slide, pendingSlideNavigationRef.current ?? undefined);
            pendingSlideNavigationRef.current = null;
            // Lock only on a real slide change. On resize (e.g. mobile address bar hiding on scroll)
            // goToSlide(currentIndex) runs with no transition, so nothing would ever unlock it.
            isTransitioning.current = true;
        }

        setNewCurrentSlide(slide);
    };

    /*
     * slide traversing - arrow click + keyboard event handlers
     */

    const handleSliderKeyDown = useCallback((e: Event) => {
        // casting as unknown because "keydown" event does not accept KeyDownEventHandler O_o . I gave up
        const keyboardEvent = e as unknown as KeyboardEvent;
        if (keyboardEvent.key === "ArrowLeft") slideLeft();
        else if (keyboardEvent.key === "ArrowRight") slideRight();
    }, []);

    const slideRight = useCallback(() => {
        if (shouldBlockEventsRef.current || isTransitioning.current) return;

        const newSlide = currentIndexRef.current + breakpointSettingsRef.current.slidesToScroll;
        // slideCount - 1 is only correct when slidesToShow is 1 - with more slides visible at once,
        // navigating that far reveals empty space past the last slide (the viewport window extends
        // beyond the slide strip). The last index that still fills the viewport with real slides is
        // slideCount - slidesToShow, floored since slide indices are integers.
        const lastReachableIndex = Math.max(slideCountRef.current - Math.floor(breakpointSettingsRef.current.slidesToShow), 0);
        if (preventInfinityScroll && newSlide > lastReachableIndex) return;

        pendingSlideNavigationRef.current = SliderGallerySlideNavigation.ARROW_NEXT;
        goToSlide(newSlide);
    }, []);

    const handleSlideRightOnClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        slideRight();
    };

    const slideLeft = useCallback(() => {
        if (shouldBlockEventsRef.current || isTransitioning.current) return;

        const newSlide = currentIndexRef.current - breakpointSettingsRef.current.slidesToScroll;
        if (preventInfinityScroll && newSlide < 0) return;

        pendingSlideNavigationRef.current = SliderGallerySlideNavigation.ARROW_PREV;
        goToSlide(newSlide);
    }, []);

    const handleSlideLeftOnClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        slideLeft();
    };

    useImperativeHandle(ref, () => ({
        goToSlide,
        slideLeft,
        slideRight
    }));

    /*
     * misc
     */

    const preventClickEventsOnGesture: MouseEventHandler = (e) => {
        // prevent click events when gesture is detected
        if (shouldBlockEventsRef.current) {
            e.preventDefault();
            e.stopPropagation();
        }
    };

    /*
     * render
     */

    const slidesToShowDynamic = breakpointSettingsRef.current.slidesToShow;
    const transitionStyle: CSSProperties = !preventAnimationRef.current
        ? {transform: `translateX(${translateValue}px)`, transition: `transform ${speed}ms ${sliderTransition}`}
        : {transform: `translateX(${translateValue}px)`};
    const slideWidthPercent = slidesToShowDynamic ? 100 / slidesToShowDynamic : 100;
    const slideWidth = `${slideWidthPercent}%`;
    const singleSlideWidth: CSSProperties = {
        flex: `0 0 ${slideWidth}`,
        maxWidth: slideWidth,
        minWidth: `max(${slideWidth}, var(--base-slider-slide-min-width, 0px))`
    };
    const galleryHolderCN = classNames(className, galleryHolder, flushEdges && galleryHolderFlush);
    const sliderCN = classNames(className, slider);
    const singleSliderCN = classNames(slideStyles, singleSlide, flushEdges && singleSlideFlush);
    const sliderWrapperCN = classNames(sliderInnerWrap, sliderWrapperClass);

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === slides.length - 1;
    const leftArrowCN = classNames(arrow, preventInfinityScroll && isFirstSlide && arrowDisabled);
    const rightArrowCN = classNames(arrow, preventInfinityScroll && isLastSlide && arrowDisabled);

    return (
        <div {...{[SLIDER_GALLERY_ROOT_ATTR]: ""}} className={galleryHolderCN} onClick={preventClickEventsOnGesture} ref={galleryHolderRef}>
            {arrows && slides.length > 1 && (
                <span onClick={handleSlideLeftOnClick} className={leftArrowCN}>
                    {arrows.left}
                </span>
            )}
            <div className={sliderWrapperCN}>
                {!isSliderLoaded ? (
                    <div ref={gallerySliderRef} className={sliderCN} style={transitionStyle}>
                        <div key={clonedSlidesCount} className={singleSliderCN} style={singleSlideWidth}>
                            {slides[initialSlide]}
                        </div>
                    </div>
                ) : (
                    <div ref={gallerySliderRef} className={sliderCN} style={transitionStyle} onTransitionEnd={handleSliderTransitionEnd}>
                        {slidesListWithClones.map((slide, index) => (
                            <div key={isSliderLoaded ? index + clonedSlidesCount : index} className={singleSliderCN} style={singleSlideWidth}>
                                {slide}
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {arrows && slides.length > 1 && (
                <span onClick={handleSlideRightOnClick} className={rightArrowCN}>
                    {arrows.right}
                </span>
            )}
        </div>
    );
});

BaseSlider.displayName = "BaseSlider";
